// Typed fetch wrapper that attaches the Rumio session token automatically.
// All API endpoints under /api/* go through this. On a 401 it asks the host
// for a fresh token and retries once; only if that fails is the session
// cleared.

import { getSessionToken, clearSession } from './session.js';
import { requestSessionRefresh } from './session-refresh.js';

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

function makeApiError(status: number, body: unknown): ApiError {
  const err = new Error(`API ${status}`) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

async function readJsonOrText(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return res.json().catch(() => null);
  }
  return res.text().catch(() => null);
}

export interface ApiOptions extends RequestInit {
  // Defaults to true. Set to false for endpoints that don't require auth
  // (none currently exist on /api/* but kept for forward-compat).
  withAuth?: boolean;
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const { withAuth = true, headers, ...rest } = opts;
  const send = (token: string | null) => {
    const finalHeaders = new Headers(headers);
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
    // FormData sets its own multipart boundary.
    if (rest.body && !(rest.body instanceof FormData) && !finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
    return fetch(path, { ...rest, headers: finalHeaders });
  };

  let res = await send(withAuth ? getSessionToken() : null);
  if (res.status === 401 && withAuth) {
    const fresh = await requestSessionRefresh();
    if (fresh) res = await send(fresh);
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const body = await readJsonOrText(res);
    if (res.status === 401) clearSession();
    throw makeApiError(res.status, body);
  }
  return (await readJsonOrText(res)) as T;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && typeof (err as ApiError).status === 'number';
}

export interface ErrorInfo {
  status: number | null;
  code: string | null;
  /** Human-readable, safe to show in the UI. */
  message: string;
  /** Plan entitlement that would unlock this, for 402 responses. */
  feature?: string;
  /** The plan's quota, for 402 plan_limit_reached. */
  limit?: number;
}

const MESSAGES: Record<string, string> = {
  plan_upgrade_required: 'This feature needs a higher plan.',
  plan_limit_reached: 'You’ve reached your plan’s limit.',
  media_required: 'Attach audio before publishing.',
  transcript_not_ready: 'The transcript is not ready yet.',
  edit_removes_everything: 'That would cut the whole episode.',
  duration_unknown: 'The audio is still being analysed. Try again in a moment.',
  invalid_id: 'That item could not be found.',
  not_found: 'That item could not be found.',
  validation_failed: 'Some fields are invalid. Check the form and try again.',
  unauthenticated: 'Your session expired. Reload the page from your site.',
};

/** Normalise anything thrown by api()/mutations into something displayable. */
export function errorInfo(err: unknown): ErrorInfo {
  if (!isApiError(err)) {
    return { status: null, code: null, message: 'Something went wrong. Please try again.' };
  }
  const body = (err.body && typeof err.body === 'object' ? err.body : {}) as {
    error?: unknown;
    feature?: unknown;
    limit?: unknown;
  };
  const code = typeof body.error === 'string' ? body.error : null;
  const message =
    (code && MESSAGES[code]) ??
    (err.status >= 500 ? 'The server had a problem. Please try again.' : 'The request could not be completed.');
  return {
    status: err.status,
    code,
    message,
    ...(typeof body.feature === 'string' ? { feature: body.feature } : {}),
    ...(typeof body.limit === 'number' ? { limit: body.limit } : {}),
  };
}
